import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV_MAIN, NAV_QUICK } from './nav';
import { SettingsIcon, LogIcon, UserIcon, ShieldIcon, MailIcon, LogoutIcon } from './icons';

function useAuth() {
  let role = 'user';
  let perms = [];

  const pushUnique = (arr, val) => {
    if (!val) return;
    if (Array.isArray(val)) { val.forEach(v => pushUnique(arr, v)); return; }
    if (typeof val === 'string') {
      val.split(/[\s,;]+/).filter(Boolean).forEach(v => { if (!arr.includes(v)) arr.push(v); });
      return;
    }
    if (typeof val === 'object') {
      Object.keys(val).forEach(k => { if (val[k]) pushUnique(arr, k); });
      return;
    }
  };

  const tryJWT = () => {
    try {
      const t = localStorage.getItem('token') || localStorage.getItem('access_token');
      if (!t) return null;
      const parts = t.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g,'+').replace(/_/g,'/')));
      return payload;
    } catch { return null; }
  };

  try {
    const raw =
      localStorage.getItem('user') ||
      localStorage.getItem('auth_user') ||
      localStorage.getItem('userInfo');

    let u = null;
    if (raw) {
      try { u = JSON.parse(raw); } catch {}
    }
    if (!u) {
      const jwt = tryJWT();
      if (jwt) u = { jwt };
    }

    if (u) {
      const extractRole = (obj) => (
        obj?.role || obj?.user_role || obj?.user?.role || obj?.data?.role ||
        obj?.jwt?.role || obj?.jwt?.role_name || obj?.jwt?.roleId || obj?.roleName ||
        (obj?.is_admin === true ? 'admin' : undefined) ||
        'user'
      );

      const collectPerms = (obj) => {
        const out = [];
        const candidates = [
          obj?.permissions, obj?.perms, obj?.scopes, obj?.capabilities,
          obj?.user?.permissions, obj?.data?.permissions,
          obj?.jwt?.permissions, obj?.jwt?.perms, obj?.jwt?.scope
        ];
        candidates.forEach(c => pushUnique(out, c));
        // also handle boolean flags like { users: true, roles_read: true }
        if (obj && typeof obj === 'object') {
          const flagKeys = Object.keys(obj).filter(k => /user|role|perm|scope/i.test(k) && obj[k] === true);
          pushUnique(out, flagKeys);
        }
        return out;
      };

      role = extractRole(u) || role;
      perms = collectPerms(u);
    }
  } catch {}

  const isAdmin =
    ['admin','owner','superadmin','root'].includes(String(role).toLowerCase()) ||
    perms.includes('admin') || perms.includes('admin:all');

  const canUsers =
    isAdmin ||
    perms.includes('users') ||
    perms.includes('users:read') ||
    perms.includes('users:manage') ||
    perms.includes('user:read') ||
    perms.includes('user:manage');

  const canRoles =
    isAdmin ||
    perms.includes('roles') ||
    perms.includes('roles:read') ||
    perms.includes('roles:manage') ||
    perms.includes('role:read') ||
    perms.includes('role:manage');

  if (process.env.NODE_ENV !== 'production') {
    try { console.info('[AuthDetect]', { role, perms, canUsers, canRoles }); } catch {}
  }
  if (process.env.REACT_APP_FORCE_ADMIN === '1') {
    return { isAdmin: true, canUsers: true, canRoles: true };
  }
  return { isAdmin: isAdmin, canUsers, canRoles };
}
export default function Sidebar({ onNavigate }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { canUsers, canRoles } = useAuth();

  /* runtime auth log */
  React.useEffect(()=>{ try{ console.log('[AuthRuntime]', { canUsers, canRoles }); }catch{} }, [canUsers, canRoles]);

  const active = (to) =>
    (to === '/dashboard' ? pathname === '/dashboard' : pathname.startsWith(to)) ? 'active' : '';

  const logout = () => {
    try {
      localStorage.removeItem('token');
    } catch {}
    navigate('/login');
  };

  return (
    <div className="sidebar-wrap">
      <div className="sidebar-group">
        <div className="title">Main</div>
        <nav className="sidebar-nav">
          {NAV_MAIN.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className={active(it.to)}
              onClick={onNavigate}
            >
              {it.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="sidebar-divider" />

      <div className="sidebar-group">
        <div className="title">Quick</div>
        <nav className="sidebar-nav">
          {NAV_QUICK.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className={active(it.to)}
              onClick={onNavigate}
            >
              {it.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="sidebar-divider" />

      <div className="sidebar-group" data-admin="1">
        <div className="title">Admin</div>
        <div className="admin-menu">
          <Link
            to="/settings"
            className={active('/settings')}
            onClick={onNavigate}
          >
            <SettingsIcon />
            <span>Settings</span>
          </Link>
          <Link
            to="/logging"
            className={active('/logging')}
            onClick={onNavigate}
          >
            <LogIcon />
            <span>Logging</span>
          </Link>
          {canUsers && (
            <Link
              to="/users"
              className={active('/users')}
              onClick={onNavigate}
            >
              <UserIcon />
              <span>User</span>
            </Link>
          )}
          {canRoles && (
            <Link to="/roles" className={active('/roles')} onClick={onNavigate}>
              <ShieldIcon />
              <span>Roles</span>
            </Link>
          )}
          <Link
            to="/contact"
            className={active('/contact')}
            onClick={onNavigate}
          >
            <MailIcon />
            <span>Contact Us</span>
          </Link>
          <button type="button" onClick={logout}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
}
